import React, { useEffect, useState } from 'react';
import AppConst from "../apis/appconst";
import axios from "axios";
import SampleModel, { buildLabelSheetHtml, openPrintWindow } from '../models/SampleModel';
import { Modal, Button, Form } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from "sweetalert2";

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import QRCode from 'qrcode';
import { toast } from 'react-toastify';

import DataTable from 'react-data-table-component';
import JsBarcode from 'jsbarcode';

// ✅ Fallback barcode config used until the backend config loads.
// All real values come from the backend (see loadBarcodeConfigFromBackend).
const DEFAULT_BARCODE_CONFIG = {
  columns: 2,        // 1 or 2 columns
  paperWidth: 80,    // paper roll width in mm
  labelWidth: 36,    // width of each label in mm
  labelHeight: 0,    // height of each label in mm (0 = auto)
  rowGap: 3,         // vertical gap to the next barcode (mm)
  columnGap: 3,      // horizontal gap between the 2 barcodes (mm) - only when columns = 2
  barWidth: 2,       // JsBarcode bar thickness
  barHeight: 40,     // JsBarcode barcode height (px)
  fontSize: 14,      // UHID text font size (px)
  showBorder: true,  // border around each label
  outerBorder: false,// border around the whole label sheet
  leftMargin: 0,     // mm — shift all labels right (fine position)
  topMargin: 0,      // mm — shift all labels down (fine position)
};

const LabRegistrations = () => {
  const today = new Date().toISOString().split("T")[0];
  const [departmentFilter, setDepartmentFilter] = useState('pathology'); // default to pathology
  const [selectedUser, setSelectedUser] = useState(null);
  const [image, setImage] = useState('');
  const [watermarkImage, setWatermarkImage] = useState('');
  const [footerImage, setFooterImage] = useState('');
  const [Name, setName] = useState('');
  const [selectedTests, setSelectedTests] = useState([]);

  // ✅ Active tab: 'labList' | 'barcodeConfig'
  const [activeTab, setActiveTab] = useState('labList');

  // ✅ Barcode config comes from backend; DEFAULT is only a fallback.
  const [barcodeConfig, setBarcodeConfig] = useState(DEFAULT_BARCODE_CONFIG);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const updateBarcodeConfig = (key, value) => {
    setBarcodeConfig(prev => ({ ...prev, [key]: value }));
  };

  // ✅ Print a sample label using the current (backend) config, to calibrate the printer.
  const printSampleBarcode = () => {
    const count = parseInt(barcodeConfig.columns) === 2 ? 2 : 1;
    const html = buildLabelSheetHtml({
      name: "SAMPLE",
      uhid: "1234567890",
      ageGender: "30Y / M",
      count,
      cfg: barcodeConfig,
    });
    openPrintWindow(html);
  };

  const [formData, setFormData] = useState({
    cat_type: 'department',
    page_no: 0,
    l1: [],
    departs: [],
    selItem: {},
    search_key: '',
    from: today,
    to: today,
    test_report_datetime: ''
  });

  const [ldata, setLdata] = useState(null);
  const [selItem, setSelItem] = useState(null);

  // ✅ Load barcode print config from backend.
  // Point AppConst.getBarcodeConfigUrl at your route; backend should return { status, config: {...} }.
  const loadBarcodeConfigFromBackend = async () => {
    try {
      if (!AppConst.getBarcodeConfigUrl) return; // endpoint not wired yet -> keep defaults
      setConfigLoading(true);
      const fd = new FormData();
      const res = await axios.post(AppConst.BASE_URL + AppConst.getBarcodeConfigUrl, fd);
      if (res.data && res.data.status && res.data.config) {
        setBarcodeConfig(prev => ({ ...prev, ...res.data.config }));
      }
    } catch (err) {
      console.error("loadBarcodeConfigFromBackend error:", err);
    } finally {
      setConfigLoading(false);
    }
  };

  // ✅ Save barcode print config to backend.
  // Point AppConst.saveBarcodeConfigUrl at your route; backend should return { status, config }.
  const saveBarcodeConfigToBackend = async () => {
    try {
      if (!AppConst.saveBarcodeConfigUrl) {
        toast.error("Save endpoint not configured (AppConst.saveBarcodeConfigUrl).");
        return;
      }
      setConfigSaving(true);
      const fd = new FormData();
      fd.append('columns', String(barcodeConfig.columns));
      fd.append('paperWidth', String(barcodeConfig.paperWidth));
      fd.append('labelWidth', String(barcodeConfig.labelWidth));
      fd.append('labelHeight', String(barcodeConfig.labelHeight));
      fd.append('rowGap', String(barcodeConfig.rowGap));
      fd.append('columnGap', String(barcodeConfig.columnGap));
      fd.append('barWidth', String(barcodeConfig.barWidth));
      fd.append('barHeight', String(barcodeConfig.barHeight));
      fd.append('fontSize', String(barcodeConfig.fontSize));
      fd.append('showBorder', barcodeConfig.showBorder ? '1' : '0');
      fd.append('outerBorder', barcodeConfig.outerBorder ? '1' : '0');
      fd.append('leftMargin', String(barcodeConfig.leftMargin));
      fd.append('topMargin', String(barcodeConfig.topMargin));

      const res = await axios.post(AppConst.BASE_URL + AppConst.saveBarcodeConfigUrl, fd);
      if (res.data && res.data.status) {
        if (res.data.config) setBarcodeConfig(prev => ({ ...prev, ...res.data.config }));
        toast.success("Barcode configuration saved.");
      } else {
        toast.error(res.data?.message || "Failed to save configuration.");
      }
    } catch (err) {
      console.error("saveBarcodeConfigToBackend error:", err);
      toast.error("Server error while saving configuration.");
    } finally {
      setConfigSaving(false);
    }
  };

  // init on mount
  useEffect(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;

    setFormData(prev => ({ ...prev, from: todayStr, to: todayStr }));
    initData({ from: todayStr, to: todayStr });

    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      try {
        setLdata(JSON.parse(storedData));
      } catch (e) {
        console.warn("Failed to parse ldata", e);
      }
    }

    loadBarcodeConfigFromBackend(); // ✅ pull print config from backend
  }, []);

  useEffect(() => {
    if (formData.from && formData.to) {
      initData();
    }
  }, [formData.from, formData.to]);

  useEffect(() => {
    const cleanup = () => {
      document.querySelectorAll('.modal-backdrop').forEach(n => n.remove());
      document.body.classList.remove('modal-open');
      document.querySelectorAll('.modal.show').forEach(m => m.classList.remove('show'));
    };

    cleanup();
    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, []);

  // ✅ AUTO-REFRESH: Refresh page every 5 minutes if no user activity
  useEffect(() => {
    let activityTimer;
    let lastActivityTime = Date.now();
    const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

    const resetActivityTimer = () => {
      lastActivityTime = Date.now();
      if (activityTimer) clearTimeout(activityTimer);
      activityTimer = setTimeout(() => {
        const now = Date.now();
        if (now - lastActivityTime >= INACTIVITY_TIMEOUT) {
          console.log("🔄 Auto-refreshing page due to inactivity...");
          window.location.reload();
        }
      }, INACTIVITY_TIMEOUT);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => document.addEventListener(event, resetActivityTimer, true));
    resetActivityTimer();

    return () => {
      if (activityTimer) clearTimeout(activityTimer);
      activityEvents.forEach(event => document.removeEventListener(event, resetActivityTimer, true));
    };
  }, []);

  const initData2 = async () => {
    const storedDataF = JSON.parse(sessionStorage.getItem("ldata") || "{}");
    setName([storedDataF?.fname, storedDataF?.lname].filter(Boolean).join(" "));
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      try {
        const fd = new FormData();
        fd.append('cat_type', "department");
        fd.append('page_no', "none");
        fd.append('search_key', "");
        fd.append('par_by', "all");

        const response = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd);
        if (response.data.status && response.data.records) {
          setFormData(prev => ({ ...prev, departs: response.data.records }));
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    initData2();
  }, []);

  const initData = async (overrides = {}) => {
    try {
      const fd = new FormData();

      const from = overrides.from !== undefined ? overrides.from : formData.from;
      const to = overrides.to !== undefined ? overrides.to : formData.to;
      const search_key = overrides.search_key !== undefined ? overrides.search_key : (formData.search_key || "");

      fd.append('page_no', 'none');
      fd.append('search_key', search_key + "");
      fd.append('from', from ? (from + " 00:00:00") : "");
      fd.append('to', to ? (to + " 23:59:59") : "");
      fd.append('lab_reg_user', 'all');

      const response = await axios.post(AppConst.BASE_URL + AppConst.getLabRegListUrl, fd);

      if (response.data && response.data.status) {
        const records = response.data.records || [];
        setFormData(prev => ({ ...prev, l1: records }));
      } else {
        setFormData(prev => ({ ...prev, l1: response.data?.records || [] }));
      }
    } catch (err) {
      console.error("initData error:", err);
      toast.error("Failed to load patient list.");
    }
  };

  useEffect(() => {
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      const parsed = JSON.parse(storedData);
      setLdata(parsed);
    }

    const storedLabDt = sessionStorage.getItem("lab_dt");
    if (storedLabDt) {
      try {
        const parsed = JSON.parse(storedLabDt);

        (async () => {
          let items = parsed.params || parsed.tests || parsed.all_tests || [];

          if ((!items || items.length === 0) && parsed.lab_reg_id) {
            items = await fetchLabItemsByRegId(parsed.lab_reg_id);
          }

          items = (items || []).map(it => ({
            ...it,
            lab_item_test_name: (it.lab_item_test_name || it.test_name || "").toString()
          }));

          const normalizedRecord = {
            ...parsed,
            tests: items,
            params: items,
            displayTestNames: items.length ? items.map(t => (t.lab_item_test_name || '').trim()).join(' || ') : (parsed.displayTestNames || '')
          };

          setFormData(prev => ({ ...prev, l1: [normalizedRecord] }));
          setSelectedUser(normalizedRecord);
          setSelectedTests((normalizedRecord.tests || normalizedRecord.params || []).map(t =>
            (t.lab_item_test_name || t.test_name || '').toString()
          ));
        })();

      } catch (e) {
        console.error("Failed to parse sessionStorage.lab_dt", e);
        initData();
      }
      return;
    }

    initData();
  }, []);

  const fetchLabItemsByRegId = async (labRegId) => {
    try {
      const fd = new FormData();
      fd.append('page_no', 'none');
      fd.append('lab_item_parent', String(labRegId));
      const res = await axios.post(AppConst.BASE_URL + AppConst.getLabRegListItmUrl, fd);
      if (res.data && res.data.status && Array.isArray(res.data.records)) return res.data.records;
      return [];
    } catch (err) {
      console.error("fetchLabItemsByRegId error:", err);
      return [];
    }
  };

  function formatDateTime(dtStr) {
    if (!dtStr) return "";
    try {
      let d = new Date(dtStr);
      if (isNaN(d.getTime())) {
        const t = String(dtStr).trim().replace(" ", "T");
        d = new Date(t);
      }
      if (isNaN(d.getTime())) return String(dtStr);

      const dd = String(d.getDate()).padStart(2, "0");
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const mon = months[d.getMonth()];
      const yyyy = d.getFullYear();

      let hh = d.getHours();
      const ampm = hh >= 12 ? "PM" : "AM";
      hh = hh % 12;
      if (hh === 0) hh = 12;
      const mm = String(d.getMinutes()).padStart(2, "0");

      return `${dd}-${mon}-${yyyy} ${String(hh).padStart(2, "0")}:${mm} ${ampm}`;
    } catch (e) {
      return String(dtStr);
    }
  }

  // ✅ Helper: does a test belong to radiology?
    // ✅ Radiology = department cat_id 60
    // ✅ Radiology = test_cat 60
const isRadiologyTest = (t) => String(t?.test_cat ?? '').trim() === '60';
  // Open Collect Sample modal (barcode printing lives inside that modal now)
  const collectSamepl = (item) => {
    try {
      setSelItem(item);
      setSelectedUser(item);
    } catch (err) {
      console.error("collectSamepl error:", err);
    }
  };

  // Reprint for already-collected samples -> also opens the same modal
  const reprintBarcode = (item) => {
    try {
      setSelItem(item);
      setSelectedUser(item);
    } catch (err) {
      console.error("reprintBarcode error:", err);
      toast.error("Failed to open barcode print");
    }
  };

  const onUpNow1 = async (id, sample_type, sample_qty, cont_type, sample_cndi) => {
    try {
      if (!selItem) return;

      const fd = new FormData();
      fd.append('lab_reg_id', String(id));
      fd.append('sample_type', String(sample_type));
      fd.append('sample_qty', String(sample_qty));
      fd.append('cont_type', String(cont_type));
      fd.append('sample_cndi', String(sample_cndi));

      try {
        const response = await axios.post(AppConst.BASE_URL + AppConst.upSampleUrl, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.status) {
          setSelItem(null);

          setFormData(prev => {
            const newL1 = (prev.l1 || []).map(x => {
              if (String(x.lab_reg_id) === String(id)) {
                return { ...x, lab_reg_status: 'Sample Collected' };
              }
              return x;
            });
            return { ...prev, l1: newL1 };
          });

          Swal.fire({
            title: "Success!",
            text: "Sample collected successfully.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          toast.error(response.data?.message || "Failed to update sample");
        }
      } catch (error) {
        console.error("Server error updating sample:", error);
        toast.error("Server error while updating sample");
      }
    } catch (error) {
      console.error("onUpNow1 error:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredList = formData.search_key.trim() === ""
    ? formData.l1
    : (formData.l1 || []).filter(item =>
      (`${item.lab_reg_uhid}`.toLowerCase().includes(formData.search_key.toLowerCase()) ||
        `${item.lab_reg_id}`.toLowerCase().includes(formData.search_key.toLowerCase()) ||
        `${item.lab_reg_name}`.toLowerCase().includes(formData.search_key.toLowerCase()))
    );

  const groupedData = React.useMemo(() => {
    const data = filteredList || [];
    if (data.length === 0) return [];

    let grouped;

    if (data[0].tests && Array.isArray(data[0].tests)) {
      grouped = data.map(patient => {
        const { tests, ...restOfPatient } = patient;
        return {
          ...restOfPatient,
          params: tests,
          displayTestNames: tests.map(test => test.lab_item_test_name.trim()).join(' || ')
        };
      });
    } else {
      const temp = Object.values(data.reduce((acc, testItem) => {
        const key = testItem.lab_reg_id;
        if (!acc[key]) {
          acc[key] = { ...testItem, all_tests: [] };
        }
        acc[key].all_tests.push(testItem);
        return acc;
      }, {}));

      grouped = temp.map(patient => ({
        ...patient,
        params: patient.all_tests,
        displayTestNames: patient.all_tests.map(t => t.lab_item_test_name.trim()).join(' || ')
      }));
    }

    // ✅ FILTER BY DEPARTMENT
    return grouped.filter(patient => {
      const testsArray = patient.params || patient.all_tests || [];
      if (departmentFilter === 'pathology') {
        return testsArray.some(t => !isRadiologyTest(t));
      } else if (departmentFilter === 'radiology') {
        return testsArray.some(t => isRadiologyTest(t));
      }
      return true; // 'all'
    });
  }, [filteredList, departmentFilter]);

  const columns = [
    {
      name: 'SR',
      selector: (row, i) => groupedData.indexOf(row) + 1,
      width: '60px'
    },
    { name: 'UHID No', selector: row => row.lab_reg_uhid || '-', sortable: true, wrap: true, width: '120px' },
    { name: 'Reg No', selector: row => row.lab_reg_id || '-', sortable: true, wrap: true, width: '100px' },
    {
      name: 'Patient Details',
      selector: row => `${row.lab_reg_name_title || ''} ${row.lab_reg_name || ''}`,
      cell: row => (
        <div>
          <div>{row.lab_reg_name_title} {row.lab_reg_name}</div>
          <small>
            <strong>Age:</strong> {row.lab_reg_age_year}y {row.lab_reg_age_month}m {row.lab_reg_age_day}d<br />
            <strong>Gender:</strong> {row.lab_reg_gender}<br />
            <strong>Mobile:</strong> {row.lab_reg_mobile}
          </small>
        </div>
      ),
      sortable: true,
      grow: 1.4,
      wrap: true,
      width: '200px'
    },
    { name: 'Refered Doctor', selector: row => row.lab_reg_referral || '-', sortable: true, wrap: true, width: '140px' },
    { name: 'Test Details', selector: row => row.displayTestNames || 'N/A', wrap: true, grow: 3, width: '200px' },
    {
      name: 'Status',
      selector: row => row.lab_reg_status,
      cell: row => {
        const status = (row.lab_reg_status || '').toString().trim().toLowerCase();
        const testsArray = row.params || row.all_tests || [];
        const allRadiology = testsArray.length > 0 && testsArray.every(t => isRadiologyTest(t));

        return (
          <div style={{ textAlign: 'center' }}>
            {status === "pending" && !allRadiology && (
              <div>
                <i className="ri-flask-fill" style={{ color: "red", fontSize: "24px" }}></i>
                <div style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => collectSamepl(row)}
                  >
                    Collect Sample
                  </button>
                </div>
              </div>
            )}

            {status === "pending" && allRadiology && (
              <div>
                <i className="ri-file-text-line" style={{ color: "orange", fontSize: "24px" }}></i>
                <div style={{ marginTop: 6, fontSize: '11px', color: '#666' }}>
                  Radiology - No Sample
                </div>
              </div>
            )}

            {status === "sample collected" && (
              <div>
                <i className="bi bi-droplet-half" style={{ color: "green", fontSize: "24px" }}></i>
                <div style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => reprintBarcode(row)}
                    title="Reprint barcode"
                  >
                    <i className="ri-printer-line me-1"></i>
                    Print Barcode
                  </button>
                </div>
              </div>
            )}

            {status === "completed" && (
              <i className="ri-check-double-line" style={{ color: "blue", fontSize: "24px" }}></i>
            )}
          </div>
        );
      },
      center: true,
      width: '190px'
    },
    {
      name: 'Patient Type',
      selector: row => row.lab_reg_type,
      center: true,
      width: '130px',
      cell: row => {
        const raw = (row.lab_reg_type || '').toString().trim().toLowerCase();
        let badgeClass = 'badge bg-secondary';
        let label = row.lab_reg_type || 'N/A';

        switch (raw) {
          case 'opd':
          case 'outpatient':
            badgeClass = 'badge bg-info text-dark';
            label = 'OPD';
            break;
          case 'ipd':
          case 'inpatient':
            badgeClass = 'badge bg-primary';
            label = 'IPD';
            break;
          case 'emergency':
          case 'er':
          case 'casualty':
            badgeClass = 'badge bg-danger';
            label = 'Emergency';
            break;
          case 'corporate':
            badgeClass = 'badge bg-success';
            label = 'Corporate';
            break;
          case 'referral':
            badgeClass = 'badge bg-warning text-dark';
            label = 'Referral';
            break;
          default:
            label = (row.lab_reg_type || 'Other').toString().replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
            badgeClass = 'badge bg-secondary';
        }

        return (
          <div style={{ textAlign: 'center' }}>
            <span className={badgeClass} style={{ padding: '6px 10px', fontSize: '0.95rem' }}>
              {label}
            </span>
          </div>
        );
      }
    },
    { name: 'Registration Datetime', selector: row => formatDateTime(row.lab_reg_datetime) || '-', wrap: true, width: '200px' },
    { name: 'Sample Collection Datetime', selector: row => formatDateTime(row.lab_reg_sample_datetime) || '-', wrap: true, width: '200px' },
  
  
  ];

  // Config-tab preview count
  const previewCount = parseInt(barcodeConfig.columns) === 2 ? 4 : 2;

  // Editable number field for the Barcode Configuration tab.
  // Allows the field to be cleared to empty (no forced 0); min is applied on blur.
  const NumField = ({ label, field, min = 0, step = 1, suffix, disabled }) => {
    const val = barcodeConfig[field];
    const shown = (val === '' || val === null || val === undefined) ? '' : val;
    return (
      <div className="col-md-4">
        <label className="form-label fw-bold">
          {label}{suffix ? ` (${suffix})` : ''}
        </label>
        <input
          type="number"
          className="form-control"
          min={min}
          step={step}
          disabled={disabled}
          value={shown}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { updateBarcodeConfig(field, ''); return; }
            const parsed = step < 1 ? parseFloat(raw) : parseInt(raw);
            updateBarcodeConfig(field, isNaN(parsed) ? '' : parsed);
          }}
          onBlur={(e) => {
            const raw = e.target.value;
            if (raw === '') return; // keep empty; save/backend will apply the default
            const parsed = step < 1 ? parseFloat(raw) : parseInt(raw);
            updateBarcodeConfig(field, isNaN(parsed) ? '' : Math.max(min, parsed));
          }}
        />
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" id="orderList">
              <div className="card-header border-0">
                <div className="row align-items-center gy-3">
                  <div className="col-sm">
                    <h2 className="card-title mb-0" style={{ color: "#405189" }}>Lab Patient Details</h2>
                  </div>
                </div>

                {/* ✅ TABS */}
                <ul className="nav nav-tabs card-header-tabs mt-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'labList' ? 'active' : ''}`}
                      onClick={() => setActiveTab('labList')}
                    >
                      <i className="ri-list-check me-1"></i> Lab List
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'barcodeConfig' ? 'active' : ''}`}
                      onClick={() => setActiveTab('barcodeConfig')}
                    >
                      <i className="ri-settings-3-line me-1"></i> Barcode Configuration
                    </button>
                  </li>
                </ul>
              </div>

              {/* ========================= LAB LIST TAB ========================= */}
              {activeTab === 'labList' && (
                <>
                  <div className="card-body border border-dashed border-end-0 border-start-0">
                    <form>
                      <div className="row g-3">
                        <div className="col-md-2">
                          <label className="form-label">From</label>
                          <input className="form-control" type="date" name="from" onChange={handleChange} value={formData.from} />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">To</label>
                          <input className="form-control" type="date" name="to" onChange={handleChange} value={formData.to} />
                        </div>

                        {/* ✅ DEPARTMENT FILTER BUTTONS */}
                        <div className="col-md-3">
                          <label className="form-label">Department</label>
                          <div className="btn-group w-100" role="group">
                            <button
                              type="button"
                              className={`btn ${departmentFilter === 'pathology' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setDepartmentFilter('pathology')}
                            >
                              <i className="ri-flask-line me-1"></i>
                              Pathology
                            </button>
                            <button
                              type="button"
                              className={`btn ${departmentFilter === 'radiology' ? 'btn-info' : 'btn-outline-info'}`}
                              onClick={() => setDepartmentFilter('radiology')}
                            >
                              <i className="ri-file-text-line me-1"></i>
                              Radiology
                            </button>
                            <button
                              type="button"
                              className={`btn ${departmentFilter === 'all' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              onClick={() => setDepartmentFilter('all')}
                            >
                              All
                            </button>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <label className="form-label"></label>
                          <div className="search-box" style={{ display: "flex" }}>
                            <button type="button" onClick={() => {
                              const today = new Date();
                              const yyyy = today.getFullYear();
                              const mm = String(today.getMonth() + 1).padStart(2, '0');
                              const dd = String(today.getDate()).padStart(2, '0');
                              const todayStr = `${yyyy}-${mm}-${dd}`;
                              setFormData(prev => ({ ...prev, search_key: "", from: todayStr, to: todayStr, page_no: 0 }));
                              setDepartmentFilter('pathology');
                              initData({ from: todayStr, to: todayStr, search_key: "" });
                            }} className="btn btn-info">Clear</button>
                          </div>
                        </div>

                        <div className="col-md-3" style={{ textAlign: "right" }}>
                          <label className="form-label"></label>
                          <div className="search-box">
                            <input className="form-control search" placeholder="Search..." type="text"
                              name="search_key" onChange={handleChange} value={formData.search_key} />
                            <i className="ri-search-line search-icon"></i>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>

                  <br />

                  <div className="card-body pt-0">
                    <div className="table-responsive table-card mb-1">
                      <DataTable
                        columns={columns}
                        data={groupedData || []}
                        pagination
                        highlightOnHover
                        pointerOnHover
                        responsive
                        dense={false}
                        noHeader
                        paginationPerPage={10}
                        striped
                        conditionalRowStyles={[
                          {
                            when: row => row.lab_reg_status === "Sample Collected",
                            style: { backgroundColor: '#e3f9e5' }
                          }
                        ]}
                        customStyles={{
                          rows: { style: { minHeight: '58px', fontSize: '16px' } },
                          headCells: {
                            style: {
                              backgroundColor: '#f1f5fb',
                              color: '#405189',
                              fontSize: '15px',
                              fontWeight: '700',
                              paddingLeft: '14px',
                              paddingRight: '14px',
                            }
                          },
                          cells: {
                            style: {
                              paddingLeft: '14px',
                              paddingRight: '14px',
                              fontSize: '15px',
                              lineHeight: '20px'
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ===================== BARCODE CONFIGURATION TAB ===================== */}
              {activeTab === 'barcodeConfig' && (
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h5 className="text-primary mb-1">
                        <i className="ri-settings-3-line me-2"></i>Barcode Print Settings
                      </h5>
                      <small className="text-muted">Edit and Save. Values are stored on the server and used for all barcode prints.</small>
                    </div>
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadBarcodeConfigFromBackend} disabled={configLoading || configSaving}>
                        <i className="ri-refresh-line me-1"></i>{configLoading ? 'Loading...' : 'Reload'}
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={printSampleBarcode} disabled={configSaving}>
                        <i className="ri-printer-line me-1"></i>Print Sample
                      </button>
                      <button type="button" className="btn btn-success btn-sm" onClick={saveBarcodeConfigToBackend} disabled={configSaving}>
                        <i className="ri-save-line me-1"></i>{configSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>

                  <div className="row">
                    {/* Editable settings */}
                    <div className="col-lg-7">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Columns</label>
                          <select
                            className="form-select"
                            value={barcodeConfig.columns}
                            onChange={(e) => updateBarcodeConfig('columns', parseInt(e.target.value))}
                          >
                            <option value={1}>1 Column</option>
                            <option value={2}>2 Columns</option>
                          </select>
                        </div>

                        <NumField label="Paper Width" field="paperWidth" min={10} suffix="mm" />
                        <NumField label="Label Width" field="labelWidth" min={5} suffix="mm" />
                        <NumField label="Label Height" field="labelHeight" min={0} suffix="mm, 0=auto" />
                        <NumField label="Row Gap" field="rowGap" min={0} suffix="mm" />
                        <NumField
                          label="Column Gap"
                          field="columnGap"
                          min={0}
                          suffix="mm"
                          disabled={parseInt(barcodeConfig.columns) === 1}
                        />
                        <NumField label="Bar Width" field="barWidth" min={1} step={0.5} />
                        <NumField label="Barcode Height" field="barHeight" min={10} suffix="px" />
                        <NumField label="Font Size" field="fontSize" min={6} suffix="px" />
                        <NumField label="Left Margin" field="leftMargin" min={0} step={0.5} suffix="mm, shift right" />
                        <NumField label="Top Margin" field="topMargin" min={0} step={0.5} suffix="mm, shift down" />

                        <div className="col-md-4 d-flex align-items-end">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="cfgShowBorder"
                              checked={!!barcodeConfig.showBorder}
                              onChange={(e) => updateBarcodeConfig('showBorder', e.target.checked)}
                            />
                            <label className="form-check-label fw-bold" htmlFor="cfgShowBorder">
                              Label Border
                            </label>
                          </div>
                        </div>
                        <div className="col-md-4 d-flex align-items-end">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="cfgOuterBorder"
                              checked={!!barcodeConfig.outerBorder}
                              onChange={(e) => updateBarcodeConfig('outerBorder', e.target.checked)}
                            />
                            <label className="form-check-label fw-bold" htmlFor="cfgOuterBorder">
                              Outer Border
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Layout preview */}
                    <div className="col-lg-5 mt-4 mt-lg-0">
                      <h6 className="text-primary mb-2">Layout Preview</h6>
                      <div className="p-3 bg-light rounded" style={{ overflowX: 'auto' }}>
                        <div
                          style={{
                            width: `${Number(barcodeConfig.paperWidth) || 80}mm`,
                            background: '#fff',
                            padding: '2mm',
                            border: barcodeConfig.outerBorder ? '1px solid #000' : '1px dashed #adb5bd',
                            display: 'grid',
                            gridTemplateColumns: `repeat(${parseInt(barcodeConfig.columns) === 1 ? 1 : 2}, ${Number(barcodeConfig.labelWidth) || 36}mm)`,
                            columnGap: `${parseInt(barcodeConfig.columns) === 2 ? (Number(barcodeConfig.columnGap) || 0) : 0}mm`,
                            rowGap: `${Number(barcodeConfig.rowGap) || 0}mm`,
                            justifyContent: 'start'
                          }}
                        >
                          {Array.from({ length: previewCount }).map((_, i) => (
                            <div
                              key={i}
                              style={{
                                width: `${Number(barcodeConfig.labelWidth) || 36}mm`,
                                height: Number(barcodeConfig.labelHeight) ? `${barcodeConfig.labelHeight}mm` : 'auto',
                                minHeight: '14mm',
                                border: barcodeConfig.showBorder ? '1px solid #000' : '1px solid #e0e0e0',
                                boxSizing: 'border-box',
                                padding: '1.5mm',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 7
                              }}
                            >
                              <div style={{ fontWeight: 'bold' }}>PATIENT NAME</div>
                              <div style={{
                                width: '90%',
                                height: '6mm',
                                margin: '1mm 0',
                                background: 'repeating-linear-gradient(90deg,#000 0 1px,#fff 1px 3px)'
                              }} />
                              <div>1234567890</div>
                              <div style={{ fontWeight: 'bold' }}>30Y / M</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <small className="text-muted d-block mt-2">
                        Label order: name → barcode → UHID → age/gender.
                      </small>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ✅ Collect Sample modal — barcode print + manual copies live inside here */}
      <SampleModel
        item={selItem}
        onUpNow={onUpNow1}
        config={barcodeConfig}
        show={!!selItem}
        onClose={() => setSelItem(null)}
        onCollected={(id) => {
          setFormData(prev => {
            const newL1 = (prev.l1 || []).map(x => {
              if (String(x.lab_reg_id) === String(id)) {
                return { ...x, lab_reg_status: 'Sample Collected' };
              }
              return x;
            });
            return { ...prev, l1: newL1 };
          });
          setSelItem(null);
        }}
      />

    </div>
  );
};

export default LabRegistrations;